import axios from "axios";
import { API_BASE_URL } from "../utils/apiConfig";
import { tokenStorage } from "./tokenStorage";

// Shared refresh state — one inflight refresh at a time across all interceptors
let refreshPromise = null;

// What refreshTokenOnce throws when there is nothing to send. Named because
// the throw and the predicate that reads the message back must not drift.
const NO_REFRESH_TOKEN = "No refresh token";

// RPAA-276 follow-up: bound how hard a stranded tab hammers a dependency that
// is already failing.
//
// Keeping the tokens on a transient failure is the fix and stays, but on its
// own it is unbounded. `originalRequest._retry` is per request object, so
// every later request restarts the refresh cycle from scratch, and with
// ConversationsWorkspace and ConversationDetail polling at POLL_MS = 10000 a
// tab whose refreshes keep failing issues one /auth/refresh every 10 seconds
// for as long as it stays open. Each of those costs the backend two serial
// uncached Zitadel round trips, 20 to 34 seconds each when Zitadel is
// degraded, so they overlap and pile up rather than serialising.
//
// This lives here, beside refreshPromise, and not in the interceptor, because
// refreshTokenOnce is the single choke point for those round trips: every
// axios instance's interceptor and conversationLiveStream's SSE 401 branch
// already funnel through it, so one piece of state bounds all of them and
// there is no parallel mechanism to keep in step with this one.
//
// It escalates rather than capping the number of attempts, because a hard cap
// leaves the tab in exactly the state this exists to end — one that only a
// manual logout gets out of. A capped backoff bounds the traffic and still
// lets the session come back on its own once Zitadel recovers.
const REFRESH_COOLDOWN_BASE_MS = 30000;
const REFRESH_COOLDOWN_MAX_MS = 300000;
let refreshCooldownUntil = 0;
let consecutiveRefreshFailures = 0;

// Dedicated axios instance for token refresh — avoids interceptor loops
const refreshAxios = axios.create();

/**
 * Tells a refresh failure that means the session is genuinely dead apart from
 * one that means the backend or the IdP is having a bad minute.
 *
 * Read in two places that have to agree. The interceptor uses it to decide
 * whether to clear the session; refreshTokenOnce uses it to decide whether to
 * arm the cooldown, and must NOT arm one for a genuinely dead session — that
 * session is being torn down, so a cooldown would only survive to block the
 * first refresh after the next login.
 */
function isGenuinelyInvalid(error) {
  return error?.response?.status === 401 || error?.message === NO_REFRESH_TOKEN;
}

export async function refreshTokenOnce() {
  // If a refresh is already in progress, return the same promise
  if (refreshPromise) return refreshPromise;

  // Still cooling down from a transient failure. Fail without touching the
  // network, so a polling tab cannot turn a degraded IdP into a self-inflicted
  // load test. Deliberately not shaped like a genuinely invalid session: the
  // caller must keep the tokens, because they may well still be good.
  const now = Date.now();
  if (now < refreshCooldownUntil) {
    throw new Error(
      `Refresh on cooldown for another ${refreshCooldownUntil - now}ms`
    );
  }

  refreshPromise = (async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) throw new Error(NO_REFRESH_TOKEN);

    const response = await refreshAxios.post(
      `${API_BASE_URL}/auth/refresh?frontend=backoffice`,
      { refresh_token: refreshToken },
      { headers: { "Content-Type": "application/json" } }
    );

    const data = response.data;
    tokenStorage.setTokens(data);

    return data.access_token;
  })();

  try {
    const accessToken = await refreshPromise;
    // One good refresh ends the cooldown outright rather than decaying it: the
    // dependency answered, so the next failure should start from the bottom.
    consecutiveRefreshFailures = 0;
    refreshCooldownUntil = 0;
    return accessToken;
  } catch (error) {
    if (!isGenuinelyInvalid(error)) {
      refreshCooldownUntil =
        Date.now() +
        Math.min(
          REFRESH_COOLDOWN_BASE_MS * 2 ** consecutiveRefreshFailures,
          REFRESH_COOLDOWN_MAX_MS
        );
      consecutiveRefreshFailures += 1;
    }
    throw error;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Dispatch a custom event so the React app (AuthContext) can handle
 * session expiry via React Router instead of a hard window.location reload.
 */
function clearTokensAndRedirect() {
  tokenStorage.clearAll();
  window.dispatchEvent(new CustomEvent("auth:session-expired"));
  // Fallback: if nobody handles the event within 100ms, do a hard redirect
  setTimeout(() => {
    if (tokenStorage.getAccessToken() === null) {
      window.location.href = "/";
    }
  }, 100);
}

/**
 * Creates a 401 response interceptor that uses the shared refresh queue.
 * Skips refresh attempts for auth endpoints to prevent infinite loops.
 * @param {import("axios").AxiosInstance} instance - The axios instance to retry with
 */
export function createRefreshInterceptor(instance) {
  return async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || "";

    // Never attempt to refresh for auth endpoints — prevents infinite loops
    const isAuthEndpoint =
      requestUrl.includes("/auth/refresh") ||
      requestUrl.includes("/auth/logout") ||
      requestUrl.includes("/auth/callback");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshTokenOnce();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        // Same distinction as AuthContext.initAuth (RPAA-276): only a
        // genuinely invalid refresh token — 401 from /auth/refresh, or none
        // stored to send at all — should force a logout. A transient
        // 500/503/network error on the refresh endpoint must not clear a
        // session that could still be perfectly good once the blip passes.
        // This interceptor sits in front of every authenticated call in the
        // app, so getting this wrong here has the widest blast radius of
        // anywhere this bug pattern showed up. What stops the kept session
        // from retrying forever is refreshTokenOnce's cooldown, not this
        // branch, which is why the rate limit lives one level down.
        if (isGenuinelyInvalid(refreshError)) {
          clearTokensAndRedirect();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };
}
